import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { service } from '@ember/service';
import { TrackedAsyncData } from 'ember-async-data';
import includes from '../helpers/includes';
import { on } from '@ember/modifier';
import { fn, array } from '@ember/helper';
import FaIcon from '@fortawesome/ember-fontawesome/components/fa-icon';
import { and, eq, not } from 'ember-truth-helpers';
import t from 'ember-intl/helpers/t';
import { LinkTo } from '@ember/routing';
import truncate from '../helpers/truncate';
import formatDate from 'ember-intl/helpers/format-date';
import PublicationStatus from './publication-status';
import {
  faArrowRightToBracket,
  faCaretDown,
  faCaretRight,
  faClipboardList,
  faTrash,
  faUserClock,
} from '@fortawesome/free-solid-svg-icons';

export default class SessionsGridSessionRowComponent extends Component {
  @service permissionChecker;
  @service intl;

  @cached
  get canDeleteData() {
    return new TrackedAsyncData(this.permissionChecker.canDeleteSession(this.args.session));
  }

  get canDelete() {
    return this.canDeleteData.isResolved ? this.canDeleteData.value : false;
  }

  @cached
  get canUpdateData() {
    return new TrackedAsyncData(this.permissionChecker.canUpdateSession(this.args.session));
  }

  get canUpdate() {
    return this.canUpdateData.isResolved ? this.canUpdateData.value : false;
  }

  @cached
  get sessionPrerequisitesData() {
    return new TrackedAsyncData(this.args.session.prerequisites);
  }

  get sessionPrerequisites() {
    return this.sessionPrerequisitesData.isResolved ? this.sessionPrerequisitesData.value : null;
  }

  get prerequisitesLabelAndTitle() {
    const titles = this.sessionPrerequisites
      .map((prereq) => prereq.title)
      .sort()
      .join(', ');
    return `${this.intl.t('general.prerequisites')}: ${titles}`;
  }
  <template>
    <div
      class="sessions-grid-row sessions-grid-session-row{{if
          (includes @session.id @sessionsForRemovalConfirmation)
          ' confirm'
        }}"
      data-test-sessions-grid-session-row
    >
      <span class="expand-collapse-control" data-test-expand-collapse-control>
        {{#if (includes @session.id @expandedSessionIds)}}
          <button
            class="link-button"
            type="button"
            aria-label={{t "general.expand"}}
            data-test-collapse
            {{on "click" (fn @closeSession @session)}}
          >
            <FaIcon @icon={{faCaretDown}} />
          </button>
        {{else}}
          <button
            class="link-button{{if (eq @session.offeringCount 0) ' disabled'}}"
            disabled={{if (eq @session.offeringCount 0) "disabled"}}
            type="button"
            aria-label={{t "general.close"}}
            title={{if (eq @session.offeringCount 0) (t "general.noOfferings")}}
            data-test-expand
            {{on "click" (fn @expandSession @session)}}
          >
            <FaIcon @icon={{faCaretRight}} />
          </button>
        {{/if}}
      </span>
      <span class="session-grid-title">
        <LinkTo
          @route="session"
          @models={{array @session.course @session}}
          aria-label={{@session.uniqueTitleInCourse}}
        >
          {{truncate @session.title 100 true}}
        </LinkTo>
      </span>
      <span class="session-grid-type">
        {{@session.sessionType.title}}
      </span>
      <span class="session-grid-groups">
        {{@session.learnerGroupCount}}
      </span>
      <span class="session-grid-objectives">
        {{@session.objectiveCount}}
      </span>
      <span class="session-grid-terms">
        {{@session.termCount}}
      </span>
      <span class="session-grid-first-offering">
        {{#if @session.firstOfferingDate}}
          {{#if @session.isIndependentLearning}}
            {{#if @session.hasPostrequisite}}
              <strong>
                {{t "general.ilm"}}:
                <FaIcon @icon={{faUserClock}} />
                {{t "general.duePriorTo"}}:
              </strong>
              <LinkTo
                @route="session"
                @models={{array @session.course.id @session.postrequisite.id}}
                title={{@session.postrequisite.title}}
              >
                {{truncate @session.postrequisite.title 18 true}}
              </LinkTo>
            {{else}}
              <strong>
                {{t "general.ilm"}}:
                {{t "general.dueBy"}}
              </strong>
              {{formatDate @session.firstOfferingDate month="2-digit" day="2-digit" year="numeric"}}
            {{/if}}
          {{else if @session.hasPostrequisite}}
            <strong>
              <FaIcon @icon={{faUserClock}} />
              {{t "general.duePriorTo"}}:
            </strong>
            <LinkTo
              @route="session"
              @models={{array @session.course.id @session.postrequisite.id}}
              title={{@session.postrequisite.title}}
            >
              {{truncate @session.postrequisite.title 18 true}}
            </LinkTo>
          {{else}}
            {{formatDate
              @session.firstOfferingDate
              month="2-digit"
              day="2-digit"
              year="numeric"
              hour12=true
              hour="2-digit"
              minute="numeric"
            }}
          {{/if}}
        {{/if}}
      </span>
      <span class="session-grid-offerings">
        {{@session.offeringCount}}
      </span>
      <span class="session-grid-status" data-test-status>
        {{#if (and this.canUpdate @session.prerequisiteCount)}}
          {{#if this.sessionPrerequisites}}
            <FaIcon
              @icon={{faArrowRightToBracket}}
              @ariaHidden={{false}}
              class="prerequisites"
              @flip="horizontal"
              @title={{this.prerequisitesLabelAndTitle}}
              data-test-prerequisites
            />
          {{/if}}
        {{/if}}
        {{#if (and this.canUpdate @session.instructionalNotes.length)}}
          <FaIcon
            @icon={{faClipboardList}}
            @ariaHidden={{false}}
            class="instructional-notes"
            @title={{t "general.instructionalNotes"}}
          />
        {{/if}}
        <PublicationStatus @item={{@session}} />
      </span>
      <span class="session-grid-actions" data-test-actions>
        {{#if (and this.canUpdate (not @session.prerequisiteCount))}}
          <button
            class="link-button delete-button{{if @showConfirmDelete ' disabled'}}"
            type="button"
            {{on "click" (fn @confirmDelete @session.id)}}
            title={{if
              @showConfirmDelete
              (t "general.disabledByConfirmation")
              (t "general.remove")
            }}
            disabled={{@showConfirmDelete}}
            data-test-delete
          >
            <FaIcon
              @icon={{faTrash}}
              @ariaHidden={{false}}
              class={{if @showConfirmDelete "disabled" "remove enabled"}}
            />
          </button>
        {{else}}
          <FaIcon
            @icon={{faTrash}}
            @ariaHidden={{false}}
            class="disabled"
            data-test-delete-disabled
            @title={{t "general.canNotDeleteSession"}}
          />
        {{/if}}
      </span>
    </div>
  </template>
}
